// A dropped file → the bytes the pool stores.
//
// The pipeline reads PCM WAV and AIFF only (shared/audio/master-mix.ts), and
// /api/uploads checks no format — so the browser guarantees it. A WAV or AIFF
// whose header that decoder reads goes up byte for byte (Live's own renders
// are AIFF; the worker rewraps them for Chrome). Everything else — FLAC, MP3,
// ADPCM WAV, compressed AIFC — is decoded by the browser AT ITS OWN SAMPLE
// RATE (an AudioContext would resample to the device's) and re-encoded as
// WAV at the source's own depth (wavDepthFor): 16-bit sources and MP3 stay
// 16-bit, deeper or unknown ones go to 24.

import type { AudioExtension } from "./config";
import { blobReader, decodedProbe, formatOf, mp3SampleRate, probeHeader, wavDepthFor, type AudioProbe } from "./probe";
import { encodePcmWav } from "./wav-out";

/** Decode with the browser, keeping the file's rate. Throws on audio it can't read. */
export async function decodeFile(blob: Blob, ext: AudioExtension, sampleRateHint: number | null): Promise<AudioBuffer> {
  const rate = sampleRateHint ?? (ext === "mp3" ? await mp3SampleRate(blobReader(blob)) : null) ?? 44100;
  const ctx = new OfflineAudioContext(1, 1, rate);
  return ctx.decodeAudioData(await blob.arrayBuffer());
}

/** The probe for a file whose header can't tell its length (MP3): decode it, measure, let it go. */
export async function probeByDecoding(blob: Blob, ext: AudioExtension): Promise<AudioProbe> {
  return decodedProbe(await decodeFile(blob, ext, null), formatOf(ext));
}

export async function toUploadBytes(blob: Blob, ext: AudioExtension, probe: AudioProbe): Promise<Uint8Array> {
  if (probe.passthrough) return new Uint8Array(await blob.arrayBuffer());
  const buffer = await decodeFile(blob, ext, probe.sampleRate);
  return encodePcmWav(buffer, wavDepthFor(probe.format, probe.bitDepth));
}

/** A file's probe: from its header when that says enough, else by decoding. Null when it isn't readable audio. */
export async function probeFile(blob: Blob, ext: AudioExtension): Promise<AudioProbe | null> {
  if (blob.size === 0) return null;
  try {
    const fromHeader = await probeHeader(ext, blob.size, blobReader(blob));
    if (fromHeader && fromHeader.durationSec > 0) return fromHeader;
    const decoded = await probeByDecoding(blob, ext);
    return decoded.durationSec > 0 ? decoded : null;
  } catch {
    return null;
  }
}
